"use client";

import { MultiSelect } from "@/components/ui/multi-select";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Check, Globe, Lock, Users } from "lucide-react";
import { isTeamVisibility } from "@/types/visibility";

export interface CommandAgentSharingCardProps {
    idPrefix: string;
    visibility: AppIntegrationVisibility;
    sharedRoles: string[];
    canListRoles: boolean;
    rolesLoading: boolean;
    roleOptions: Array<{ label: string; value: string }>;
    errors: { visibility: string; shared_roles: string };
    onVisibilityChange: (visibility: AppIntegrationVisibility) => void;
    onSharedRolesChange: (roles: string[]) => void;
    /** When true, render inline without an outer card (for prototype General panel). */
    embedded?: boolean;
}

function CommandAgentSharingFields({
    visibility,
    sharedRoles,
    canListRoles,
    rolesLoading,
    roleOptions,
    errors,
    onVisibilityChange,
    onSharedRolesChange,
}: Omit<CommandAgentSharingCardProps, "idPrefix" | "embedded">) {
    const isTeam = isTeamVisibility(visibility);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
                {[
                    {
                        value: "PERSONAL" as const,
                        icon: Lock,
                        label: "Personal",
                        description: "Only you can see this Agent.",
                    },
                    {
                        value: "TEAM" as const,
                        icon: Globe,
                        label: "Team",
                        description: "Anyone in your organisation can discover and use this agent.",
                    },
                ].map(({ value, icon: Icon, label, description }) => {
                    const active = visibility === value;
                    return (
                        <button
                            key={label}
                            type="button"
                            onClick={() => onVisibilityChange(value)}
                            className={cn(
                                "relative flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-200",
                                active
                                    ? "border-primary/30 bg-primary/[0.04] shadow-[0_0_0_1px_hsl(var(--primary)/0.2)]"
                                    : "border-border/40 bg-background hover:border-border hover:bg-muted/30"
                            )}
                        >
                            <div
                                className={cn(
                                    "flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors",
                                    active
                                        ? "bg-primary/10 text-primary"
                                        : "bg-muted/60 text-muted-foreground"
                                )}
                            >
                                <Icon className="size-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p
                                    className={cn(
                                        "text-sm font-semibold leading-none",
                                        active ? "text-foreground" : "text-muted-foreground"
                                    )}
                                >
                                    {label}
                                </p>
                                <p className="mt-1 text-xs text-muted-foreground leading-snug">
                                    {description}
                                </p>
                            </div>
                            <div
                                className={cn(
                                    "flex size-4 shrink-0 items-center justify-center rounded-full border-2 transition-all",
                                    active ? "border-primary bg-primary" : "border-border/50"
                                )}
                            >
                                {active ? (
                                    <Check className="size-2 text-primary-foreground" strokeWidth={3} />
                                ) : null}
                            </div>
                        </button>
                    );
                })}
            </div>

            {errors.visibility ? (
                <p className="text-xs text-destructive">{errors.visibility}</p>
            ) : null}

            {isTeam && canListRoles ? (
                <div className="space-y-2 rounded-xl border border-border/40 bg-muted/20 px-4 py-3">
                    <div className="flex items-center gap-2">
                        <Users className="size-4 text-muted-foreground" />
                        <p className="text-sm font-medium">Restrict to roles</p>
                        <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                            Optional
                        </span>
                    </div>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                        Leave empty to allow all users. Select roles to limit access to specific groups.
                    </p>
                    <MultiSelect
                        options={roleOptions}
                        value={sharedRoles}
                        onValueChange={onSharedRolesChange}
                        placeholder={
                            rolesLoading
                                ? "Loading roles…"
                                : roleOptions.length === 0
                                  ? "No roles available"
                                  : "Select one or more roles"
                        }
                        disabled={rolesLoading || roleOptions.length === 0}
                        className="border-border/40"
                    />
                    {errors.shared_roles ? (
                        <p className="text-xs text-destructive">{errors.shared_roles}</p>
                    ) : null}
                </div>
            ) : null}

            {isTeam && !canListRoles ? (
                <p className="rounded-xl border border-border/40 bg-muted/20 px-4 py-3 text-xs leading-relaxed text-muted-foreground">
                    You don&apos;t have permission to list roles. Ask an administrator to grant access if
                    you need to restrict this agent by role.
                </p>
            ) : null}
        </div>
    );
}

export function CommandAgentSharingCard({
    embedded = false,
    ...props
}: CommandAgentSharingCardProps) {
    if (embedded) {
        return <CommandAgentSharingFields {...props} />;
    }

    return (
        <Card className="border-border/40 shadow-sm">
            <CardHeader className="px-4 pb-3 sm:px-6">
                <CardTitle className="text-base font-medium">Visibility & sharing</CardTitle>
                <CardDescription>
                    Control who can access this agent across your organisation.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 px-4 pb-6 sm:px-6">
                <CommandAgentSharingFields {...props} />
            </CardContent>
        </Card>
    );
}
